import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowRight, CheckCircle, Play, Star } from 'lucide-react';

/**
 * Professional Hero Section
 * Clean, corporate design with blue/grey palette
 * Bilingual (EN ↔ ES) via react-i18next namespace `home`.
 */
export default function ProfessionalHero({ onAnalyze, analyzing }) {
  const { t } = useTranslation(['home']);
  const [websiteUrl, setWebsiteUrl] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (websiteUrl && onAnalyze) {
      onAnalyze(websiteUrl);
    }
  };

  const features = t('home:hero.features', { returnObjects: true, defaultValue: [] });
  const stats = t('home:hero.stats', { returnObjects: true, defaultValue: [] });
  const receiveItems = t('home:hero.receive.items', { returnObjects: true, defaultValue: [] });

  return (
    <section className="relative bg-gradient-to-b from-slate-50 to-white py-16 lg:py-24 overflow-hidden">
      {/* Subtle background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-100/50 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-slate-100 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left">
            {/* Trust Badge */}
            <div className="inline-flex items-center gap-2 bg-slate-100 rounded-full px-4 py-2 mb-6">
              <div className="flex -space-x-1">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="w-6 h-6 rounded-full bg-slate-300 border-2 border-white" />
                ))}
              </div>
              <span className="text-sm text-slate-600">
                {t('home:hero.trusted')}
              </span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
              {t('home:hero.headlinePrefix')}{' '}
              <span className="text-blue-600">{t('home:hero.headlineAccent')}</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg sm:text-xl text-slate-600 mb-8 max-w-xl mx-auto lg:mx-0">
              {t('home:hero.subheadline')}
            </p>

            {/* Feature List */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-4 mb-8">
              {features.map((feature) => (
                <div key={feature} className="flex items-center gap-2 text-slate-700">
                  <CheckCircle className="h-5 w-5 text-blue-600" />
                  <span className="text-sm font-medium">{feature}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
              <Link to="/consultation">
                <Button size="lg" className="bg-slate-900 hover:bg-slate-800 text-white px-8 py-6 text-lg" data-testid="hero-consult-btn">
                  {t('home:hero.ctaPrimary')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/portfolio">
                <Button size="lg" variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-100 px-8 py-6 text-lg" data-testid="hero-portfolio-btn">
                  <Play className="mr-2 h-5 w-5" />
                  {t('home:hero.ctaSecondary')}
                </Button>
              </Link>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-slate-200">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center lg:text-left">
                  <div className="text-2xl sm:text-3xl font-bold text-slate-900">{stat.value}</div>
                  <div className="text-sm text-slate-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Content - Website Analyzer */}
          <div className="lg:pl-8">
            <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6 sm:p-8">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-slate-900 mb-2">
                  {t('home:hero.analyzer.title')}
                </h2>
                <p className="text-slate-600">
                  {t('home:hero.analyzer.subtitle')}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t('home:hero.analyzer.urlLabel')}
                  </label>
                  <Input
                    type="url"
                    placeholder="https://yourwebsite.com"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    className="h-12 text-lg border-slate-300 focus:border-blue-500 focus:ring-blue-500"
                    required
                    data-testid="hero-analyzer-input"
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 text-lg"
                  disabled={analyzing}
                  data-testid="hero-analyzer-submit"
                >
                  {analyzing ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      {t('home:hero.analyzer.analyzing')}
                    </>
                  ) : (
                    <>
                      {t('home:hero.analyzer.submit')}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
              </form>

              {/* What You'll Get */}
              <div className="mt-6 pt-6 border-t border-slate-200">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-3">{t('home:hero.receive.title')}</p>
                <div className="grid grid-cols-2 gap-3">
                  {receiveItems.map((item) => (
                    <div key={item} className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Trust Indicators */}
              <div className="mt-6 pt-6 border-t border-slate-200 flex items-center justify-center gap-6">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  ))}
                  <span className="text-sm text-slate-600 ml-2">{t('home:hero.rating')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
